import { ClassNames } from "@emotion/core";
import { BasePlacement } from "@popperjs/core";
import { ThemeKey } from "@smartsheet/design-tokens";
import React, { forwardRef, ReactNode } from "react";
import { TransitionStatus } from "react-transition-group/Transition";
import { useTheme } from "../../../theme/utils/useTheme";
import { TooltipContainer, TooltipContainerProps } from "../components/TooltipContainer";
import { tooltipTheme } from "../tooltipTheme";

const slideTransitionStyles = {
    top: {
        transform: "translateY(4px)",
    },
    bottom: {
        transform: "translateY(-4px)",
    },
    left: {
        transform: "translateX(4px)",
    },
    right: {
        transform: "translateX(-4px)",
    },
};

const extraPaddingStyles = {
    top: "Bottom",
    bottom: "Top",
    left: "Right",
    right: "Left",
};

export interface TooltipFlyoutContainerProps extends TooltipContainerProps {
    /**
     * The children of this component
     */
    children: ReactNode;

    /**
     * The actual (not necessarily the requested) direction the Tooltip is placed
     * relative to its target.
     */
    enterDirection: BasePlacement | "auto";

    /**
     * For transitions, we need to wait until the Tooltip is positioned initially,
     * then we can enable the transition. Otherwise it will start from the wrong position.
     *
     */
    showTransition: boolean;

    transitionStatus: TransitionStatus;

    /**
     * passed in via popperProps to determine the size and offset of
     * the invisible wrapper div for smoother trigger UX
     */
    offset?: [number, number];
}

/**
 * This component's job is to apply the correct animations and border radius to the Tooltip.
 */
export const TooltipFlyoutContainer = forwardRef<HTMLDivElement, TooltipFlyoutContainerProps>(
    ({ children, enterDirection, showTransition, transitionStatus, offset, ...props }, ref) => {
        const { key } = useTheme();
        const themeMode = key as ThemeKey;

        const slideTransition = `transform ${tooltipTheme.motion.slide(themeMode)}`;
        const opacityTransition = `opacity ${tooltipTheme.motion.opacity(themeMode)}`;

        const wrapperSize = () => {
            if (offset) {
                if (enterDirection.includes("top") || enterDirection.includes("bottom")) {
                    return offset[1];
                }
                return offset[0];
            }
            return "8";
        };

        return (
            <ClassNames>
                {({ css }) => (
                    <div
                        className={css({
                            ...(enterDirection !== "auto"
                                ? {
                                      [`padding${extraPaddingStyles[enterDirection]}`]: `${wrapperSize()}px`,
                                      // do not remove this margin, doing so will cause an infinite loop of popper updates
                                      // why you ask? the jury is still out, but popper's looking real guilty like.
                                      [`margin${extraPaddingStyles[enterDirection]}`]: `-${wrapperSize()}px`,
                                  }
                                : {}),
                        })}
                    >
                        <TooltipContainer
                            {...props}
                            ref={ref}
                            className={css({
                                opacity: transitionStatus === "entered" ? 1 : 0,
                                transform:
                                    transitionStatus === "entering" && enterDirection !== "auto"
                                        ? slideTransitionStyles[enterDirection].transform
                                        : "translateY(0) translateX(0)",
                                // Always show opacity, slide transition nees to happen AFTER the first popper update.
                                transition: `${opacityTransition}, ${showTransition ? slideTransition : ""}`,
                            })}
                        >
                            {children}
                        </TooltipContainer>
                    </div>
                )}
            </ClassNames>
        );
    }
);
TooltipFlyoutContainer.displayName = "TooltipFlyoutContainer";
